import React, { useState, useEffect } from "react";
import { useForm } from "shared/hooks/form-hook";
import { VALIDATOR_REQUIRE } from "shared/util/validators";
import Button from "shared/components/FormElements/Button";
import CustomSelect from "shared/components/FormElements/Select";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import InfoCard from "./InfoCard";
import { SERVICE_TYPES, ROUTE_TYPES_INCLUDING_BOTH } from "@routebot/constants";

const PassengerSelection = ({
  schoolName,
  serviceType,
  maxPassengersPerRoute,
  routeType,
  routeTypeLabel,
  allPassengersInSchool,
  allSelectedPassengers = [],
  onUpdatePassengers,
  selectLabel,
  addButtonText,
  emptyStateText,
  selectedPassengersTitle,
  // cards
  maxPassengersCardTitle,
  routeTypeCardTitle,
  selectedPassengersCountTitle,
  // add all button
  addAllButtonText,
  // sections
  notFoundHeader,
  notFoundDescription,
  multipleHeader,
  multipleDescription,
  noLocationHeader,
  noLocationDescription,
  // modal
  addAllModalTitle,
  addAllModalText,
  modalCancelText,
  modalAddText,
  // toasts
  toastNoLocation,
  toastAdded,
  toastAlreadySelected,
}) => {
  const [selectedPassengers, setSelectedPassengers] = useState([]);
  const [notFoundPassengers, setNotFoundPassengers] = useState([]);
  const [multiplePassengers, setMultiplePassengers] = useState([]);
  const [noLocationPassengers, setNoLocationPassengers] = useState([]);
  const [showNotFoundSection, setShowNotFoundSection] = useState(true);
  const [showMultipleSection, setShowMultipleSection] = useState(true);
  const [showNoLocationSection, setShowNoLocationSection] = useState(true);
  const [showAddAllModal, setShowAddAllModal] = useState(false);

  const checkIfPassengerHasLocation = (passenger) => {
    if (passenger.readerStatus === "not-found" || passenger.readerStatus === "multiple") {
      return true;
    }
    let doesUserHaveLocation = true;
    if (serviceType === SERVICE_TYPES.STUDENT) {
      if (routeType === ROUTE_TYPES_INCLUDING_BOTH.PICK_UP || routeType === ROUTE_TYPES_INCLUDING_BOTH.BOTH) {
        if (!passenger?.locationgeton?.lat || !passenger?.locationgeton?.lng) {
          doesUserHaveLocation = false;
        }
        if (routeType === ROUTE_TYPES_INCLUDING_BOTH.DROP_OFF || routeType === ROUTE_TYPES_INCLUDING_BOTH.BOTH) {
          if (!passenger?.locationgetoff?.lat || !passenger?.locationgetoff?.lng) {
            doesUserHaveLocation = false;
          }
        }
      }
    } else if (serviceType === SERVICE_TYPES.PERSONEL) {
      if (!passenger?.location?.lat || !passenger?.location?.lng) {
        doesUserHaveLocation = false;
      }
    }
    return doesUserHaveLocation;
  };

  useEffect(() => {
    setShowNotFoundSection(true);
    setShowMultipleSection(true);
    setShowNoLocationSection(true);
    if (allSelectedPassengers?.length > 0) {
      setNotFoundPassengers(allSelectedPassengers.filter((passenger) => passenger?.readerStatus === "not-found"));
      setMultiplePassengers(allSelectedPassengers.filter((passenger) => passenger?.readerStatus === "multiple"));
      setNoLocationPassengers(
        allSelectedPassengers.filter(
          (passenger) => passenger?.readerStatus === "no-location" || !checkIfPassengerHasLocation(passenger)
        )
      );

      const okPassengers = allSelectedPassengers.filter(
        (passenger) => !passenger?.readerStatus || passenger?.readerStatus === "ok"
      );

      setSelectedPassengers(okPassengers);
    } else if (allSelectedPassengers?.length === 0) {
      setSelectedPassengers([]);
      setNotFoundPassengers([]);
      setMultiplePassengers([]);
      setNoLocationPassengers([]);
    }
  }, [allSelectedPassengers]);

  const [formState, inputHandler] = useForm(
    {
      passenger: { value: null, isValid: false },
    },
    false
  );

  const passengerOptions = allPassengersInSchool.map((passenger) => ({
    value: passenger.id,
    label: passenger.name,
  }));

  const addPassenger = () => {
    if (formState.inputs.passenger?.value) {
      const passengerValue = formState.inputs.passenger.value;

      if (typeof passengerValue === "string") {
        const passengerOption = passengerOptions.find((option) => option.value === passengerValue);
        if (passengerOption && !allSelectedPassengers?.find((passenger) => passenger.id === passengerOption.value)) {
          const foundPassenger = allPassengersInSchool.find((passenger) => passenger.id === passengerValue);

          let doesUserHaveLocation = true;

          if (serviceType === SERVICE_TYPES.STUDENT) {
            if (routeType === ROUTE_TYPES_INCLUDING_BOTH.PICK_UP || routeType === ROUTE_TYPES_INCLUDING_BOTH.BOTH) {
              if (!foundPassenger?.locationgeton?.lat || !foundPassenger?.locationgeton?.lng) {
                doesUserHaveLocation = false;
              }
              if (routeType === ROUTE_TYPES_INCLUDING_BOTH.DROP_OFF || routeType === ROUTE_TYPES_INCLUDING_BOTH.BOTH) {
                if (!foundPassenger?.locationgetoff?.lat || !foundPassenger?.locationgetoff?.lng) {
                  doesUserHaveLocation = false;
                }
              }
            }
          } else if (serviceType === SERVICE_TYPES.PERSONEL) {
            if (!foundPassenger?.location?.lat || !foundPassenger?.location?.lng) {
              doesUserHaveLocation = false;
            }
          }

          if (!doesUserHaveLocation) {
            foundPassenger.readerStatus = "no-location";
            const updatedNoLocationPassengers = [...allSelectedPassengers, foundPassenger].sort((a, b) =>
              a.name.localeCompare(b.name, "tr", { sensitivity: "base" })
            );

            onUpdatePassengers(updatedNoLocationPassengers);

            inputHandler("passenger", null, false);
            window.toast.error(toastNoLocation);
          } else {
            foundPassenger.readerStatus = "ok";
            const updatedPassengers = [...allSelectedPassengers, foundPassenger].sort((a, b) =>
              a.name.localeCompare(b.name, "tr", { sensitivity: "base" })
            );

            onUpdatePassengers(updatedPassengers);

            inputHandler("passenger", null, false);
            window.toast.success(toastAdded);
          }
        } else {
          window.toast.error(toastAlreadySelected);
        }
      }
    }
  };

  const removePassenger = (passengerId) => {
    onUpdatePassengers(allSelectedPassengers.filter((passenger) => passenger.id !== passengerId));
  };

  const addAllPassengers = () => {
    const existingPassengerIds = allSelectedPassengers.map((p) => p.id);
    const newPassengers = allPassengersInSchool.filter((passenger) => !existingPassengerIds.includes(passenger.id));

    newPassengers.forEach((passenger) => {
      const doesUserHaveLocation = checkIfPassengerHasLocation(passenger);
      if (!doesUserHaveLocation) {
        passenger.readerStatus = "no-location";
      }
      if (doesUserHaveLocation) {
        passenger.readerStatus = "ok";
      }
    });

    const allPassengers = [...allSelectedPassengers, ...newPassengers].sort((a, b) =>
      a.name.localeCompare(b.name, "tr", { sensitivity: "base" })
    );

    onUpdatePassengers(allPassengers);
    setShowAddAllModal(false);
    // window.toast.success("Tüm öğrenciler eklendi");
  };

  return (
    <div style={{ height: "100%", maxHeight: "100%" }}>
      <div style={{ display: "flex", gap: "16px", marginBottom: "24px" }}>
        <InfoCard icon="🏫" title={schoolName} flex={3} variant="school" />

        <InfoCard title={maxPassengersCardTitle} value={maxPassengersPerRoute} flex={2} />

        <InfoCard title={routeTypeCardTitle} value={routeTypeLabel} flex={1} />

        <InfoCard title={selectedPassengersCountTitle} value={allSelectedPassengers?.length || 0} flex={1} />
      </div>

      <div
        style={{
          marginBottom: "20px",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <div
          style={{
            display: "flex",
            width: "100%",
            gap: "15px",
            alignItems: "center",
            maxWidth: "850px",
          }}
        >
          <div style={{ flex: 2 }}>
            <CustomSelect
              options={passengerOptions}
              id="passenger"
              onInput={inputHandler}
              label={selectLabel}
              initialValue={null}
              validators={[VALIDATOR_REQUIRE()]}
              isMulti={false}
            />
          </div>
          <Button
            type="button"
            danger
            style={{ minWidth: "120px", height: "40px", marginTop: "12px" }}
            onClick={addPassenger}
            disabled={!formState.inputs.passenger?.value || selectedPassengers.length === allPassengersInSchool.length}
          >
            {addButtonText}
          </Button>
        </div>
        <div style={{ textAlign: "center", marginTop: "30px" }}>
          <Button
            type="button"
            size="small"
            danger
            style={{
              minWidth: "140px",
              height: "40px",
            }}
            onClick={() => setShowAddAllModal(true)}
            disabled={allPassengersInSchool.length === 0 || selectedPassengers.length === allPassengersInSchool.length}
          >
            {addAllButtonText}
          </Button>
        </div>
      </div>

      {notFoundPassengers?.length > 0 && showNotFoundSection && (
        <div style={{ marginTop: "10px", width: "100%" }}>
          <div
            style={{
              padding: "16px",
              backgroundColor: "#f8d7da",
              border: "1px solid #f5c6cb",
              borderRadius: "8px",
              borderLeft: "4px solid #dc3545",
              position: "relative",
            }}
          >
            <IconButton
              onClick={() => {
                setShowNotFoundSection(false);
                onUpdatePassengers(
                  allSelectedPassengers.filter((passenger) => !(passenger.readerStatus === "not-found"))
                );
              }}
              sx={{
                position: "absolute",
                top: "8px",
                right: "8px",
                color: "#721c24",
                "&:hover": {
                  backgroundColor: "rgba(114, 28, 36, 0.1)",
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <div style={{ display: "flex", alignItems: "center", marginBottom: "12px" }}>
              <span style={{ fontSize: "20px", marginRight: "8px", color: "#dc3545" }}>❌</span>
              <h4 style={{ margin: 0, color: "#721c24", fontSize: "16px", fontWeight: "600" }}>
                {notFoundHeader} ({notFoundPassengers.length})
              </h4>
            </div>
            <p style={{ margin: 0, marginBottom: "10px", color: "#721c24", fontSize: "14px", fontWeight: "400" }}>
              {notFoundDescription}
            </p>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                gap: "8px",
              }}
            >
              {notFoundPassengers.map((passenger) => (
                <div
                  key={passenger.name}
                  style={{
                    padding: "8px 12px",
                    backgroundColor: "white",
                    borderRadius: "4px",
                    border: "1px solid #f5c6cb",
                    fontSize: "14px",
                    color: "#721c24",
                  }}
                >
                  {passenger.name}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {multiplePassengers?.length > 0 && showMultipleSection && (
        <div style={{ marginTop: "20px", maxWidth: "100%" }}>
          <div
            style={{
              padding: "16px",
              backgroundColor: "#fff3cd",
              border: "1px solid #ffeaa7",
              borderRadius: "8px",
              borderLeft: "4px solid #ffc107",
              position: "relative",
            }}
          >
            <IconButton
              onClick={() => {
                setShowMultipleSection(false);
                onUpdatePassengers(
                  allSelectedPassengers.filter((passenger) => !(passenger.readerStatus === "multiple"))
                );
              }}
              sx={{
                position: "absolute",
                top: "8px",
                right: "8px",
                color: "#856404",
                "&:hover": {
                  backgroundColor: "rgba(133, 100, 4, 0.1)",
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <div style={{ display: "flex", alignItems: "center", marginBottom: "12px" }}>
              <span style={{ fontSize: "20px", marginRight: "8px", color: "#856404" }}>⚠️</span>
              <h4 style={{ margin: 0, color: "#856404", fontSize: "16px", fontWeight: "600" }}>
                {multipleHeader} ({multiplePassengers.length})
              </h4>
            </div>
            <p style={{ margin: 0, marginBottom: "10px", color: "#856404", fontSize: "14px", fontWeight: "400" }}>
              {multipleDescription}
            </p>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                gap: "8px",
              }}
            >
              {multiplePassengers.map((passenger) => (
                <div
                  key={passenger.name}
                  style={{
                    padding: "8px 12px",
                    backgroundColor: "white",
                    borderRadius: "4px",
                    border: "1px solid #ffeaa7",
                    fontSize: "14px",
                    color: "#856404",
                  }}
                >
                  {passenger.name}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {noLocationPassengers?.length > 0 && showNoLocationSection && (
        <div style={{ marginTop: "20px", maxWidth: "100%" }}>
          <div
            style={{
              padding: "16px",
              backgroundColor: "#f8d7da",
              border: "1px solid #f5c6cb",
              borderRadius: "8px",
              borderLeft: "4px solid #dc3545",
              position: "relative",
            }}
          >
            <IconButton
              onClick={() => {
                setShowNoLocationSection(false);
                onUpdatePassengers(
                  allSelectedPassengers.filter((passenger) => !(passenger.readerStatus === "no-location"))
                );
              }}
              sx={{
                position: "absolute",
                top: "8px",
                right: "8px",
                color: "#721c24",
                "&:hover": {
                  backgroundColor: "rgba(114, 28, 36, 0.1)",
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <div style={{ display: "flex", alignItems: "center", marginBottom: "12px" }}>
              <span style={{ fontSize: "20px", marginRight: "8px", color: "#721c24" }}>📍</span>
              <h4 style={{ margin: 0, color: "#721c24", fontSize: "16px", fontWeight: "600" }}>
                {noLocationHeader} ({noLocationPassengers.length})
              </h4>
            </div>
            <p style={{ margin: 0, marginBottom: "10px", color: "#721c24", fontSize: "14px", fontWeight: "400" }}>
              {noLocationDescription}
            </p>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                gap: "8px",
              }}
            >
              {noLocationPassengers.map((passenger) => (
                <div
                  key={passenger.id || passenger.name}
                  style={{
                    padding: "8px 12px",
                    backgroundColor: "white",
                    borderRadius: "4px",
                    border: "1px solid #f5c6cb",
                    fontSize: "14px",
                    color: "#721c24",
                  }}
                >
                  {passenger.name}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <hr style={{ margin: "20px 0" }} />

      {selectedPassengers?.length > 0 && (
        <div style={{ marginTop: "20px", maxWidth: "100%" }}>
          <h4 style={{ margin: 0, color: "#856404", fontSize: "18px", fontWeight: "600" }}>
            {selectedPassengersTitle}
          </h4>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: "15px",
              padding: "20px",
              marginTop: "10px",
              backgroundColor: "#f8f9fa",
              borderRadius: "8px",
              border: "1px solid #e9ecef",
              maxHeight: "100%",
              overflow: "auto",
            }}
          >
            {selectedPassengers.map((passenger) => (
              <div
                key={passenger.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px",
                  backgroundColor: "white",
                  borderRadius: "6px",
                  border: "1px solid #dee2e6",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: "500",
                      fontSize: "14px",
                      color: "#333",
                      marginBottom: "4px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {passenger.name && passenger.name.length > 30
                      ? `${passenger.name.substring(0, 30)}...`
                      : passenger.name}
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {(passenger.identifier || "-").length > 30
                      ? `${(passenger.identifier || "-").substring(0, 30)}...`
                      : passenger.identifier || "-"}
                  </div>
                </div>
                <IconButton
                  aria-label="delete"
                  size="small"
                  onClick={() => removePassenger(passenger.id)}
                  sx={{
                    marginLeft: "8px",
                    color: "#dc3545",
                    "&:hover": {
                      backgroundColor: "#f8d7da",
                    },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedPassengers?.length === 0 && (
        <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
          <p>{emptyStateText}</p>
        </div>
      )}

      {showAddAllModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "24px",
              borderRadius: "8px",
              maxWidth: "400px",
              width: "90%",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
            }}
          >
            <h3 style={{ margin: "0 0 16px 0", color: "#333" }}>{addAllModalTitle}</h3>
            <p style={{ margin: "0 0 20px 0", color: "#666", lineHeight: "1.5" }}>{addAllModalText}</p>
            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
              <Button
                type="button"
                style={{
                  backgroundColor: "#6c757d",
                  color: "white",
                  border: "1px solid #6c757d",
                  minWidth: "80px",
                }}
                onClick={() => setShowAddAllModal(false)}
              >
                {modalCancelText}
              </Button>
              <Button type="button" danger style={{ minWidth: "80px" }} onClick={addAllPassengers}>
                {modalAddText}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PassengerSelection;
